import { asNumber } from "./kernelFormat.js";

// Machine-readable render contracts. Start with the primitives whose permissive
// fallbacks can turn a bad field binding into a plausible-looking chart. Other
// kinds remain "unknown" until their contracts are added; callers must not
// mistake lack of a contract for successful validation.
export const KERNEL_RENDER_CONTRACTS = Object.freeze({
    bars: Object.freeze({
        data: "rows",
        maxItemsField: "maxItems",
        defaultMaxItems: 10,
        fields: Object.freeze({
            labelField: Object.freeze({ required: true, value: "displayable" }),
            valueField: Object.freeze({ required: true, value: "number" }),
        }),
    }),
    donut: Object.freeze({
        data: "scalar",
        fields: Object.freeze({
            valueField: Object.freeze({ required: false, value: "number", fallback: "value" }),
            maxField: Object.freeze({ required: false, value: "number" }),
        }),
    }),
});

function issue(code, path, message) {
    return { code, path, message };
}

export function validateRenderSpec(spec) {
    if (!spec || typeof spec !== "object" || Array.isArray(spec)) {
        return [issue("invalid_spec", "render", "render must be a JSON object")];
    }
    const contract = KERNEL_RENDER_CONTRACTS[spec.kind];
    if (!contract) return [];
    const issues = [];
    for (const [fieldName, field] of Object.entries(contract.fields)) {
        const value = spec[fieldName];
        if (field.required && (typeof value !== "string" || !value.trim())) {
            issues.push(issue(
                "missing_field",
                `render.${fieldName}`,
                `${spec.kind} requires a non-empty ${fieldName}`,
            ));
        } else if (value != null && (typeof value !== "string" || !value.trim())) {
            issues.push(issue(
                "invalid_field",
                `render.${fieldName}`,
                `${spec.kind} ${fieldName} must be a non-empty string when provided`,
            ));
        }
    }
    return issues;
}

function noData(data) {
    if (data == null) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    return false;
}

// A row satisfies a field contract when the bound key carries a usable value.
// "number" must parse. "displayable" only has to be *present*: a null or
// empty-string label is a real value the store keeps on purpose (an
// unattributed `client_surface` is ""), and the primitives render it with a
// placeholder. What this contract still catches is the bad binding — a key
// no row carries — which is what turns a chart into a plausible-looking lie.
function usable(row, key, type) {
    if (type === "number") return asNumber(row[key]) != null;
    return Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined;
}

export function inspectKernelRender(spec, data) {
    const specIssues = validateRenderSpec(spec);
    if (specIssues.length) return { status: "invalid_render", issues: specIssues };

    const contract = KERNEL_RENDER_CONTRACTS[spec?.kind];
    if (!contract) return { status: "unknown", issues: [] };
    if (noData(data)) return { status: "no_data", issues: [] };

    if (contract.data === "rows") {
        if (!Array.isArray(data)) {
            return {
                status: "invalid_render",
                issues: [issue("invalid_data_shape", "renderData", `${spec.kind} requires an array of rows`)],
            };
        }
        const maxItems = Math.max(
            1,
            spec[contract.maxItemsField] || contract.defaultMaxItems || data.length,
        );
        const renderedRows = data.slice(0, maxItems);
        const issues = [];
        for (const [fieldName, field] of Object.entries(contract.fields)) {
            const key = spec[fieldName];
            if (!key) continue;
            const badIndex = renderedRows.findIndex((row) =>
                !row || typeof row !== "object" || !usable(row, key, field.value));
            if (badIndex >= 0) {
                issues.push(issue(
                    field.value === "number" ? "non_numeric_field" : "unusable_field",
                    `renderData[${badIndex}].${key}`,
                    `${spec.kind} requires ${fieldName} "${key}" to ${field.value === "number" ? "contain numbers" : "be present on every row"}`,
                ));
            }
        }
        return issues.length
            ? { status: "invalid_render", issues }
            : { status: "renderable", issues: [] };
    }

    if (contract.data === "scalar") {
        if (typeof data === "number") return { status: "renderable", issues: [] };
        if (!data || typeof data !== "object" || Array.isArray(data)) {
            return {
                status: "invalid_render",
                issues: [issue("invalid_data_shape", "renderData", `${spec.kind} requires a number or an object`)],
            };
        }
        const issues = [];
        for (const [fieldName, field] of Object.entries(contract.fields)) {
            const key = spec[fieldName] || field.fallback;
            if (!key) continue;
            if (!usable(data, key, field.value)) {
                issues.push(issue(
                    "non_numeric_field",
                    `renderData.${key}`,
                    `${spec.kind} requires ${fieldName || "value"} "${key}" to contain a number`,
                ));
            }
        }
        return issues.length
            ? { status: "invalid_render", issues }
            : { status: "renderable", issues: [] };
    }

    return { status: "unknown", issues: [] };
}

export function formatRenderIssues(issues) {
    return (issues || []).map((entry) => entry.message).join("; ");
}
